import React, { useState, useEffect } from 'react';
import { ArrowRight, Download, GraduationCap, Globe, Users, Landmark, ChevronDown } from 'lucide-react';
import { Link } from 'react-router-dom';
import ApplicationForm from './ApplicationForm';

const heroStats = [
  { value: '1500+', label: 'Partner Universities', icon: GraduationCap },
  { value: '21+', label: 'Countries', icon: Globe },
  { value: '1000+', label: 'Students Guided', icon: Users },
  { value: '$100M+', label: 'Loans Facilitated', icon: Landmark }
];

const Hero = () => {
  const [applyFormOpen, setApplyFormOpen] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  useEffect(() => {
    const img = new Image();
    img.src = '/Main.jpg';
    img.onload = () => setImageLoaded(true);
  }, []);

  const downloadBrochure = () => {
    const link = document.createElement('a');
    link.href = '/docs/xpert-advisor.pdf';
    link.download = 'Xpert-Advisor-Brochure.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <>
      <section className="relative overflow-hidden min-h-[92vh] flex flex-col justify-center pt-28 pb-16">
        {/* Background image (kept) with readability gradient */}
        {/*
          Background image (kept).
          Mobile/tablet: bg-cover bg-center — the image autofits and fills the hero.
          Desktop (lg+): 100% auto, top — full image width always visible, zoom-safe.
        */}
        <div
          className={`absolute inset-0 transition-opacity duration-700 bg-no-repeat bg-cover bg-center lg:bg-[length:100%_auto] lg:bg-top ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
          style={{
            backgroundImage: 'url(/Main.jpg)',
            backgroundColor: '#f7fafd'
          }}
        >
          {/* Readability gradient — lighter than before so the photo shows through more.
              Slightly stronger on mobile where text spans the full width. */}
          <div className="absolute inset-0 bg-gradient-to-r from-white/85 via-white/55 to-white/25 lg:from-white/80 lg:via-white/40 lg:to-transparent"></div>
          <div className="absolute inset-x-0 bottom-0 h-[35%] bg-gradient-to-t from-white via-white/55 to-transparent"></div>
        </div>

        {/* Placeholder while image loads */}
        {!imageLoaded && (
          <div className="absolute inset-0 bg-gradient-to-br from-navy-50 via-white to-brand-50"></div>
        )}

        {/* Content */}
        <div className="relative z-10 w-full max-w-[1920px] mx-auto px-4 sm:px-6 lg:px-12">
          <div className="max-w-2xl lg:max-w-3xl">
            <div className="animate-fade-up [animation-delay:0.05s]">
              <span className="inline-flex items-center gap-2 rounded-full bg-navy-900/90 text-white px-4 py-1.5 text-xs font-semibold tracking-wide backdrop-blur-sm shadow-card">
                <span className="w-2 h-2 rounded-full bg-brand-400 animate-pulse"></span>
                Study Abroad · Workforce · Global Careers
              </span>
            </div>

            <h1 className="animate-fade-up [animation-delay:0.15s] mt-6 font-display font-extrabold text-navy-950 leading-[1.05] text-4xl sm:text-5xl lg:text-6xl xl:text-7xl">
              Your Gateway to
              <span className="block mt-1 text-brand-500 relative">
                Global Education
                <svg
                  className="absolute -bottom-3 left-0 w-56 sm:w-72 text-brand-400"
                  viewBox="0 0 300 12"
                  fill="none"
                  aria-hidden="true"
                >
                  <path d="M2 9C60 3 180 1 298 6" stroke="currentColor" strokeWidth="4" strokeLinecap="round" />
                </svg>
              </span>
            </h1>

            <p className="animate-fade-up [animation-delay:0.25s] mt-8 text-base sm:text-lg text-navy-800/90 max-w-xl leading-relaxed font-medium">
              From shortlisting universities to landing in your new city — admissions,
              visas, education loans and careers, guided end-to-end by experts who've
              helped 1000+ students rise above limits.
            </p>

            <div className="animate-fade-up [animation-delay:0.35s] mt-9 flex flex-wrap items-center gap-4">
              <button onClick={() => setApplyFormOpen(true)} className="btn-primary text-base px-8">
                Find Your University
                <ArrowRight className="w-4 h-4" />
              </button>
              <button onClick={downloadBrochure} className="btn-outline-navy text-base px-8">
                <Download className="w-4 h-4" />
                Brochure
              </button>
            </div>

            <div className="animate-fade-up [animation-delay:0.45s] mt-7 flex flex-wrap gap-2.5">
              <Link to="/mbbs-abroad" className="chip hover:bg-brand-50 hover:text-brand-600 transition-colors">MBBS Abroad →</Link>
              <Link to="/phd-admissions" className="chip hover:bg-brand-50 hover:text-brand-600 transition-colors">PhD Admissions →</Link>
              <Link to="/nursing-in-germany" className="chip hover:bg-brand-50 hover:text-brand-600 transition-colors">Nursing in Germany →</Link>
              <Link to="/workforce-management" className="chip hover:bg-brand-50 hover:text-brand-600 transition-colors">Workforce →</Link>
            </div>
          </div>

          {/* Stats strip */}
          <div className="animate-fade-up [animation-delay:0.55s] mt-14 lg:mt-20 grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 max-w-4xl">
            {heroStats.map((stat) => (
              <div key={stat.label} className="glass rounded-2xl px-5 py-4 flex items-center gap-3.5">
                <span className="w-10 h-10 rounded-xl bg-navy-900 text-brand-400 flex items-center justify-center shrink-0">
                  <stat.icon className="w-5 h-5" />
                </span>
                <span>
                  <span className="block font-display font-bold text-navy-900 text-lg sm:text-xl leading-none">{stat.value}</span>
                  <span className="block text-[11px] sm:text-xs font-semibold text-slate-500 mt-1">{stat.label}</span>
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Scroll cue */}
        <a
          href="#services"
          className="absolute bottom-5 left-1/2 -translate-x-1/2 z-10 hidden md:flex flex-col items-center text-navy-400 hover:text-brand-500 transition-colors"
          aria-label="Scroll to services"
        >
          <ChevronDown className="w-6 h-6 animate-bounce" />
        </a>
      </section>

      <ApplicationForm open={applyFormOpen} onOpenChange={setApplyFormOpen} />
    </>
  );
};

export default Hero;
